<template lang="pug" src="./layout.pug"></template>

<style src="./layout.scss" rel="stylesheet/scss" lang="scss" scoped></style>

<script>
import { mapState, mapGetters } from 'vuex'
import ResizeMixin from './mixin/ResizeHandler'
import { processNotification, notificationIcon } from '@/utils/helpers'
import { resetState } from '@/store/index'
import purchasePromotional from '../../components/PurchasePromotional'
import vClickOutside from 'v-click-outside'

export default {
    name: 'Layout',
    directives: {
        clickOutside: vClickOutside.directive
    },
    components: {
        'v-purchase-promotional': purchasePromotional
    },
    mixins: [ResizeMixin],
    data() {
        return {
            drawerClass: null,
            drawer: false,
            currentLang: '',
            locales: [],
            isLoggedIn: null,
            rightDrawer: false,
            exit: false
        }
    },
    computed: {
        ...mapGetters(['user', 'permissions', 'allDataFilled']),
        ...mapState({
            type: state => state.app.type,
            notifications: state => state.systemNotifications.notifications,
            purchasePromo: state => state.data.purchasePromoDialog,
            user: state => state.app.user
        }),
        routeName: function() {
            const { name } = this.$route

            const routeNameAssoc = {
                referralCodes: this.type == 'leader'
                    ? 'businessReferralCodes'
                    : 'leaderReferralCodes',
                business: this.type == 'leader'
                    ? 'yourBusinesses'
                    : 'business',
                referral: this.type == 'admin'
                    ? 'referral'
                    : 'customersBase'
            }

            return routeNameAssoc[name] || name
        },
        sortedMenu: function() {
            return this.menu.slice().sort((a, b) => (a.sorted[this.type] > b.sorted[this.type]) ? 1 : -1)
        },
        availableNotifications: function() {
            return !!this.notifications.items && this.notifications.items.slice(0, 4)
        },
        notificationsParams: function() {
            const params = {
                admin: ['withdrawRequest'],
                leader: ['newBusiness', 'emailConfirmed', 'resetFee'],
                business: ['newClient', 'resetFee']
            }

            return {
                types: params[this.type],
                status: 'unread'
            }
        },
        limitsTitle() {
            if (!this.user || !this.user.limitedTo || this.user.processedAfterRestrictions) {
                return null
            }

            const date = new Date(this.user.limitedTo).format('{dd}.{MM}.{yyyy}')

            return this.$vuetify.t(`$vuetify.authorManagement`, date)
        },
        menu() {
            return [
                {
                    name: 'payments',
                    to: { path: '/payments' },
                    icon: 'attach_money',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-payments',
                    sorted: this.generateSort(1, null, null)
                },
                {
                    name: 'card',
                    to: { path: '/card' },
                    icon: 'payment',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-cards',
                    sorted: this.generateSort(2, null, null)
                },
                {
                    name: 'unallocatedClients',
                    to: { path: '/unallocated-clients' },
                    icon: 'attach_money',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-unallocated-clients',
                    sorted: this.generateSort(3, null, null)
                },
                {
                    name: 'clientRefillHistory',
                    to: { path: '/client-refill-history' },
                    icon: 'history',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-client-refill-history',
                    sorted: this.generateSort(4, null, null)
                },
                {
                    name: 'balanceHistory',
                    to: { path: '/balance-history' },
                    icon: 'account_balance_wallet',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-balance-history',
                    showSubGroup: true,
                    sorted: this.generateSort(5, null, null)
                },
                {
                    name: 'notification',
                    to: { path: '/notification' },
                    icon: 'notifications',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'send-push-notifications',
                    sorted: this.generateSort(6, null, null)
                },
                {
                    name: 'bonusSystems',
                    to: { path: '/*' },
                    icon: 'card_giftcard',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: ['watch-cashback-system', 'watch-client-groups'],
                    sorted: this.generateSort(6, null, null),
                    children: [
                        {
                            name: 'groups',
                            to: { path: '/groups' },
                            icon: 'group',
                            roles: ['admin'],
                            permission: 'watch-client-groups',
                            loggedIn: true
                        },
                        {
                            name: 'cashbackSystems',
                            to: { path: '/cashback' },
                            icon: 'settings_backup_restore',
                            permission: 'watch-cashback-system',
                            roles: ['admin'],
                            loggedIn: true
                        }
                    ]
                },
                {
                    name: 'clientNews',
                    to: { path: '/*' },
                    icon: 'feed',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: ['client-news'],
                    sorted: this.generateSort(6, null, null),
                    children: [
                        {
                            name: 'addClientArticle',
                            to: { path: '/add-client-article' },
                            icon: 'send',
                            roles: ['admin'],
                            loggedIn: true,
                            permission: 'client-news'
                        },
                        {
                            name: 'articleList',
                            to: { path: '/client-news' },
                            icon: 'newspaper',
                            roles: ['admin'],
                            loggedIn: true,
                            permission: 'client-news'
                        }
                    ]
                },
                {
                    name: 'admins',
                    to: { path: '/admins' },
                    icon: 'person_pin',
                    roles: ['admin'],
                    permission: 'edit-admins',
                    loggedIn: true,
                    sorted: this.generateSort(6, null, null)
                },
                {
                    name: 'customers',
                    to: { path: '/customers' },
                    icon: 'person',
                    roles: ['admin'],
                    permission: 'client-news',
                    loggedIn: true,
                    sorted: this.generateSort(6, null, null)
                },
                {
                    name: 'permissions',
                    to: { path: '/permissions' },
                    icon: 'lock',
                    roles: ['admin'],
                    permission: 'change-admin-permissions',
                    loggedIn: true,
                    sorted: this.generateSort(8, null, null)
                },
                {
                    name: 'rolesManaging',
                    to: { path: '/*' },
                    icon: 'assignment_ind',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'edit-roles',
                    sorted: this.generateSort(9, null, null),
                    children: [
                        {
                            name: 'roles',
                            to: { path: '/roles' },
                            icon: 'list',
                            roles: ['admin'],
                            loggedIn: true
                        },
                        {
                            name: 'createRole',
                            to: { path: '/roles/editor' },
                            icon: 'add_circle',
                            roles: ['admin'],
                            loggedIn: true
                        }
                    ]
                },
                {
                    name: 'dashboard',
                    to: { path: '/dashboard' },
                    icon: 'insert_chart_outlined',
                    roles: ['leader', 'business'],
                    loggedIn: true,
                    sorted: this.generateSort(null, 1, 1)
                },
                {
                    name: 'systemLogs',
                    to: { path: '/system-logs' },
                    icon: 'access_time',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-system-logs',
                    sorted: this.generateSort(10, null, null)
                },
                {
                    name: 'leaders',
                    to: { path: '/leaders' },
                    icon: 'supervisor_account',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'watch-leaders',
                    sorted: this.generateSort(11, null, null)
                },
                {
                    name: this.type === 'admin' ? 'business' : 'yourBusinesses',
                    to: { path: '/business' },
                    icon: 'person',
                    roles: ['admin', 'leader'],
                    loggedIn: true,
                    permission: 'watch-business',
                    sorted: this.generateSort(12, 4, null)
                },
                {
                    name: this.type === 'admin' ? 'referral' : 'customersBase',
                    to: { path: '/referral' },
                    icon: 'sync',
                    roles: ['admin', 'business', 'leader'],
                    loggedIn: true,
                    permission: 'watch-referrals',
                    sorted: this.generateSort(13, 5, 4)
                },
                {
                    name: 'news',
                    to: { path: '/news' },
                    icon: 'rss_feed',
                    roles: ['admin', 'leader', 'business'],
                    loggedIn: true,
                    permission: 'create-news',
                    sorted: this.generateSort(14, 4, 3)
                },
                {
                    name: 'systemNotifications',
                    to: { path: '/system-notifications' },
                    icon: 'notifications',
                    roles: ['leader', 'business', 'admin'],
                    loggedIn: true,
                    sorted: this.generateSort(15, 6, 5)
                },
                {
                    name: 'accountBalanceHistory',
                    to: { path: '/account-balance-history' },
                    icon: 'account_balance_wallet',
                    roles: ['admin', 'leader', 'business'],
                    loggedIn: true,
                    permission: 'watch-account-balance-history',
                    sorted: this.generateSort(16, 3, 3)
                },
                {
                    name: 'createAccount',
                    to: { path: '/create-account' },
                    icon: 'person_add',
                    roles: ['admin'],
                    loggedIn: true,
                    permission: 'create-account',
                    sorted: this.generateSort(17, null, null)
                },
                {
                    name: 'payouts',
                    to: { path: '/payouts' },
                    icon: 'shopping_cart',
                    roles: ['admin', 'leader', 'business'],
                    loggedIn: true,
                    permission: 'edit-withdraws',
                    sorted: this.generateSort(18, 7, 7)
                },
                {
                    name: 'moderation',
                    to: { path: '/moderation' },
                    icon: 'verified_user',
                    roles: ['admin'],
                    loggedIn: true,
                    sorted: this.generateSort(19, null, null)
                },
                {
                    name: 'reports',
                    to: { path: '/extended-reports' },
                    icon: 'description',
                    roles: ['admin', 'leader'],
                    loggedIn: true,
                    permission: 'watch-extended-reports',
                    sorted: this.generateSort(20, 8, 6),
                    children: []
                },
                {
                    name: 'login',
                    to: { path: '/login' },
                    icon: 'account_circle',
                    roles: ['admin', 'business', 'leader'],
                    loggedIn: false,
                    sorted: this.generateSort(1, 1, 1)
                },
                {
                    name: 'referralCodes',
                    to: { path: '/referral-codes' },
                    icon: 'scatter_plot',
                    roles: ['leader', 'business'],
                    loggedIn: true,
                    sorted: this.generateSort(null, 6, 4)
                },
                {
                    name: 'settings',
                    to: { path: '/settings' },
                    icon: 'settings',
                    roles: ['admin', 'leader', 'business'],
                    loggedIn: true,
                    sorted: this.generateSort(21, 9, 8)
                }
            ].filter(it => typeof it.hidden == 'boolean' ? !it.hidden : true)
        },
        showValidateMessage() {
            return !this.allDataFilled && !this.exit
        },
        validateData() {
            const data = {
                'cancelled': {
                    icon: 'report',
                    textKey: 'cancelled',
                    styles: {
                        background: 'error',
                        text: 'white--text',
                        icon: 'white'
                    }
                },
                'check': {
                    icon: 'info',
                    textKey: 'check',
                    styles: {
                        background: 'info',
                        text: 'white--text',
                        icon: 'white'
                    }
                },
                'filling': {
                    icon: 'warning',
                    textKey: 'filling',
                    styles: {
                        background: 'warning',
                        text: 'black--text',
                        icon: 'black'
                    }
                }
            }

            return data[this.user.status] || data['filling']
        }
    },
    watch: {
        rightDrawer: function(value) {
            if (!value && this.availableNotifications) {
                const ids = this.availableNotifications.map('id')
                this.$store.dispatch('systemNotifications/readNotifications', {
                    ids, params: this.notificationsParams
                })
                this.drawerClass = null
            }
        }
    },
    beforeMount: function() {
        window.addEventListener('keydown', (event) => {
            if (event.ctrlKey && event.which === 192) {
                this.drawer = !this.drawer
            }
        })
    },
    created: function() {
        this.currentLang = this.$vuetify.lang.current
        this.locales = Object.keys(this.$vuetify.lang.locales)
        this.$store.dispatch('systemNotifications/getNotifications', this.notificationsParams)
    },
    methods: {
        itemLocale: function(item) {
            let name = item.name

            if (name === 'referralCodes') {
                name = this.type === 'leader' ? 'businessReferralCodes' : 'leaderReferralCodes'
            }

            return this.$vuetify.t(`$vuetify.menu.${name}`)
        },
        changeLocale: function(lang) {
            if (this.locales.includes(lang)) {
                this.currentLang = lang
                this.$vuetify.lang.current = lang
                this.$store.commit('SET_LANGUAGE', lang)
            }
        },
        isShow(types) {
            return types.includes(this.type)
        },
        isShowGroup: function(route) {
            if (!route.children) {
                return true
            } else {
                return !route.children.map('roles').flat().includes(this.type)
            }
        },
        showRoutes: function(route) {
            const isShow = route && route.loggedIn ? this.$store.getters.loggedIn : !this.$store.getters.loggedIn
            const { permission } = route
            let hasPermission = true

            if (this.type === 'admin' && permission) {
                const check = (prop) => this.permissions && this.permissions.includes(prop)

                hasPermission = Array.isArray(permission) ? permission.some(it => check(it)) : check(permission)
            }

            if (this.type) {
                return route.roles.includes(this.type) && isShow && hasPermission
            }

            return isShow
        },
        logout: async function() {
            this.exit = true

            const res = await this.$store.dispatch('logout')

            if (res && res.error) {
                this.exit = false
            } else {
                resetState()
            }
        },
        generateSort(admin, leader, business) {
            return {
                admin,
                leader,
                business
            }
        },

        referralCodesName: function() {
            return this.type === 'leader' ? 'businessReferralCodes' : 'leaderReferralCodes'
        },

        translateNotification: function(notification) {
            const userData = processNotification(notification)
            return this.$vuetify.t(`$vuetify.pages.SystemNotifications.${notification.type}`)
                .replace('{content}', userData)
                .replace('{currency}', notification.text && notification.text.sum / 100)
        },

        toggleNotifications: function() {
            if (this.notifications.items.length) {
                this.rightDrawer = !this.rightDrawer
                this.drawerClass = 'hidden'
            }
        },

        togglePurchasePromo(event) {
            this.$store.commit('SET_PURCHASE_PROMO_DIALOG', !this.purchasePromo)
        },

        notificationIcon
    }
}
</script>
